import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getEquipmentAlerts } from '../../actions/equipmentAlertActions';
import { getFacility } from '../../actions/manageLocationActions';
import { initInventory } from '../../actions/manageInventoryActions';
import { toggleContactUsModal } from '../../actions/userActions';
import { Iuser, IEquipmentAlertState } from '../../models';
import { IinitialState } from '../../reducers';
import { getUsersActiveCountry } from '../../reducers/userReducer';
import { getActiveLocalCountry } from '../../reducers/manageUserReducer';
import { getServiceUrlByCountry } from '../../helpers/urlHelpers';
import { constants } from '../../constants/constants';

interface TileButton {
    text: string;
    link?: string;
    isExternal?: boolean;
    onClick?: () => void;
}

interface DashboardTileProps {
    title: string;
    subtitle: string;
    buttons: TileButton[];
    className: string;
    showAlert?: boolean;
}

const DashboardTile: React.FC<DashboardTileProps> = ({
    title,
    subtitle,
    buttons,
    className,
    showAlert = false
}) => {
    return (
        <div className={`dashboard-tile ${className}`}>
            {showAlert && (
                <div className="dashboard-tile-alert">
                    <span className="alert-icon">!</span>
                </div>
            )}
            <div className="dashboard-tile-content">
                <h2 className="dashboard-tile-title">{title}</h2>
                <p className="dashboard-tile-subtitle">{subtitle}</p>
                <div className="dashboard-tile-buttons">
                    {buttons.map((button, index) => {
                        if (button.onClick) {
                            return (
                                <button
                                    key={index}
                                    onClick={button.onClick}
                                    className="dashboard-tile-button"
                                >
                                    {button.text}
                                </button>
                            );
                        } else if (button.isExternal && button.link) {
                            return (
                                <a
                                    key={index}
                                    href={button.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="dashboard-tile-button"
                                >
                                    {button.text}
                                </a>
                            );
                        } else if (button.link) {
                            return (
                                <Link
                                    key={index}
                                    to={button.link}
                                    className="dashboard-tile-button"
                                >
                                    {button.text}
                                </Link>
                            );
                        }
                        return null;
                    })}
                </div>
            </div>
        </div>
    );
};

interface DashboardLandingProps {
    user: Iuser;
    equipmentAlert: IEquipmentAlertState;
    selectedFacilityID: string;
    usersActiveCountry: string;
    activeLocalCountry: string;
    getEquipmentAlerts: (facilityID: string, userID: string) => void;
    getFacility: (facilityID: string) => void;
    initInventory: typeof initInventory;
    toggleContactUsModal: () => void;
}

const DashboardLanding: React.FC<DashboardLandingProps> = ({
    user,
    equipmentAlert,
    selectedFacilityID,
    usersActiveCountry,
    activeLocalCountry,
    getEquipmentAlerts: fetchEquipmentAlerts,
    getFacility: fetchFacility,
    initInventory: loadInventory,
    toggleContactUsModal: openContactUsModal
}) => {
    // Get active country - prioritize local selection over global user country
    const getActiveCountry = (): string => {
        if (activeLocalCountry !== '') {
            return activeLocalCountry;
        } else {
            return usersActiveCountry;
        }
    };

    const activeCountryID = getActiveCountry();

    // Load inventory data to populate productsByID for pages that need it (e.g., Asset Photos)
    useEffect(() => {
        if (
            selectedFacilityID &&
            constants.hasSecurityFunction(user, [
                constants.securityFunctions.ViewInventory.id
            ])
        ) {
            loadInventory(selectedFacilityID, () => {
                return;
            });
        }
    }, [selectedFacilityID, user, loadInventory]);

    // Fetch equipment alerts and facility data when component mounts, user changes, or facility selection changes
    useEffect(() => {
        if (user?.id && user?.facilities?.length > 0) {
            // Use the selected facility ID from Redux state, or fall back to first facility
            let facilityID = selectedFacilityID;

            // If no facility is selected, use the first facility from user's facilities
            if (!facilityID && user.facilities.length > 0) {
                facilityID = user.facilities[0].id;
            }

            // Only fetch if we have a valid facility ID
            if (facilityID) {
                fetchEquipmentAlerts(facilityID, user.id);
                fetchFacility(facilityID);
            }
        }
        // eslint-disable-next-line
    }, [
        // eslint-disable-next-line
        user?.id,
        // eslint-disable-next-line
        user?.facilities,
        selectedFacilityID,
        fetchEquipmentAlerts,
        fetchFacility
    ]);
    return (
        <div className="dashboard-landing">
            <div className="dashboard-landing-container">
                <DashboardTile
                    title="My Equipment"
                    subtitle="Compliance status and connected devices"
                    buttons={[
                        {
                            text: 'View Equipment',
                            link: '/dashboard/equipment'
                        }
                    ]}
                    className="equipment-tile"
                    showAlert={(() => {
                        const shouldShow = equipmentAlert?.hasAlerts || false;

                        return shouldShow;
                    })()}
                />
                <DashboardTile
                    title="My Inspections & Maintenance"
                    subtitle="Inspections, Maintenance, History"
                    buttons={[
                        {
                            text: 'View History',
                            link: '/dashboard/service-grid'
                        }
                    ]}
                    className="service-tile"
                />
                <DashboardTile
                    title="My Training"
                    subtitle="Standards and Continuing Education"
                    buttons={[
                        {
                            text: 'View Trainings',
                            link:
                                'https://shop.beaconmedaes.com/en-US/categories?utm_source=mmg&utm_medium=referral',
                            isExternal: true
                        },
                        {
                            text: 'Training Portal',
                            link: 'http://learning.beaconmedaes.com/',
                            isExternal: true
                        }
                    ]}
                    className="training-tile"
                />
                <DashboardTile
                    title="My Service Provider"
                    subtitle="Services Offered, and Company Info"
                    buttons={[
                        {
                            text: 'View Services',
                            link: getServiceUrlByCountry(activeCountryID),
                            isExternal: true
                        },
                        {
                            text: 'Contact Us',
                            onClick: openContactUsModal
                        }
                    ]}
                    className="library-tile"
                />
            </div>
        </div>
    );
};

const mapStateToProps = (state: IinitialState) => ({
    user: state.user,
    equipmentAlert: state.equipmentAlert,
    selectedFacilityID: state.selectedFacilityID,
    usersActiveCountry: getUsersActiveCountry(state),
    activeLocalCountry: getActiveLocalCountry(state)
});

const mapDispatchToProps = {
    getEquipmentAlerts,
    getFacility,
    initInventory,
    toggleContactUsModal
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardLanding);
